import { Component, Input, OnInit, OnDestroy, NgZone } from '@angular/core';
import { AlertController, ModalController, ToastController } from '@ionic/angular';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { Metodosui } from '../../services/metodosui';
import { firstValueFrom } from 'rxjs';
import { AdminCrudPage } from '../../pages/ajustes/admin-crud/admin-crud.page';
import { ComponentesGenericosComponent } from '../componentes-genericos/componentes-genericos.component';

@Component({
  selector: 'app-inspeccionedit',
  templateUrl: './inspeccionedit.component.html',
  styleUrls: ['./inspeccionedit.component.scss'],
  standalone: false
})
export class InspeccioneditComponent implements OnInit, OnDestroy {

  @Input() mode: 'add' | 'edit' = 'add';
  @Input() inspeccionData: any = null;

  // Wizard step (1=Solicitante, 2=Cliente, 3=Dirección, 4=Materiales)
  currentStep: number = 1;
  readonly totalSteps = 4;
  stepDirection: 'forward' | 'back' = 'forward';
  isKeyboardActive: boolean = false;

  // Listeners nativos del teclado (arrow functions para poder removerlos)
  private readonly _onKeyboardShow = () => this.ngZone.run(() => { this.isKeyboardActive = true; });
  private readonly _onKeyboardHide = () => this.ngZone.run(() => { this.isKeyboardActive = false; });

  // Método de respaldo para entornos web/desktop donde no hay Capacitor nativo
  setKeyboardActive(active: boolean) {
    this.isKeyboardActive = active;
  }

  // Form Data
  formData = {
    id: 0,
    cliente: '',
    direccion: '',
    solicitante: '',
    estado: 'Borrador',
    fecha_creacion: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0],
    incluyeMaterial: false
  };

  isSaving = false;

  // Catálogos e IDs seleccionados
  clientesList: any[] = [];
  selectedClienteId: number = 0;

  direccionesList: any[] = [];
  selectedDireccionId: number = 0;

  solicitantesList: any[] = [];
  selectedSolicitanteId: number = 0;

  topSolicitantes: any[] = [];
  clientesFiltradosPorSolicitante: any[] = [];
  topDirecciones: any[] = [];

  getClientesDisponibles(): any[] {
    if (this.selectedSolicitanteId) {
      // Buscar clientes del solicitante
      const matches = this.solicitantesList.filter(s => s.id === this.selectedSolicitanteId && s.clienteId > 0);
      const clientIds = Array.from(new Set(matches.map(m => m.clienteId)));
      const filtered = this.clientesList.filter(c => clientIds.includes(c.id));
      if (filtered.length > 0) {
        return filtered;
      }
    }
    return this.clientesList;
  }

  getSolicitantesDisponibles(): any[] {
    const uniqueMap = new Map<number, any>();
    for (const s of this.solicitantesList) {
      if (!uniqueMap.has(s.id)) {
        uniqueMap.set(s.id, s);
      }
    }
    return Array.from(uniqueMap.values());
  }

  getTopSolicitantesDisponibles(): any[] {
    return this.topSolicitantes;
  }

  getUsuario(): string {
    return localStorage.getItem('user_pGarcia') || 'admin';
  }

  async loadTopDirecciones(clienteId: number) {
    try {
      const data = await firstValueFrom(this.javapiService.getTopDirecciones(clienteId, this.getUsuario()));
      if (Array.isArray(data)) {
        // Mapeamos el top histórico y filtramos solo aquellas que existan en la lista permitida por cliente y grupo
        let filteredTop = data
          .map(d => ({
            nombre: d.dt1,
            frecuencia: Number(d.dt2) || 0
          }))
          .filter(topD => this.direccionesList.some(catD => catD.nombre.toLowerCase().trim() === topD.nombre.toLowerCase().trim()));

        // Si la lista de frecuentes queda muy corta (menos de 5), completamos con las direcciones permitidas del catálogo
        if (filteredTop.length < 5) {
          this.direccionesList.forEach(catD => {
            if (!filteredTop.some(f => f.nombre.toLowerCase().trim() === catD.nombre.toLowerCase().trim())) {
              filteredTop.push({ nombre: catD.nombre, frecuencia: 0 });
            }
          });
        }

        this.topDirecciones = filteredTop.slice(0, 10);
      } else {
        // Fallback: si falla la consulta o viene vacía, poblar con las direcciones permitidas del catálogo
        this.topDirecciones = this.direccionesList.slice(0, 10).map(d => ({ nombre: d.nombre, frecuencia: 0 }));
      }
    } catch (e) {
      console.error('Error al cargar direcciones frecuentes, usando catálogo:', e);
      this.topDirecciones = this.direccionesList.slice(0, 10).map(d => ({ nombre: d.nombre, frecuencia: 0 }));
    }
  }

  selectTopDireccion(nombre: string) {
    this.formData.direccion = nombre;

    // Vincular el ID del catálogo si existe
    const match = this.direccionesList.find(d => d.nombre.toLowerCase().trim() === nombre.toLowerCase().trim());
    if (match) {
      this.selectedDireccionId = match.id;
    } else {
      this.selectedDireccionId = 0;
    }
  }


  async loadTopSolicitantes() {
    try {
      const data = await firstValueFrom(this.javapiService.getTopSolicitantes(this.getUsuario()));
      if (Array.isArray(data)) {
        this.topSolicitantes = data.map(s => ({
          nombre: s.dt1,
          frecuencia: Number(s.dt2) || 0
        }));
      }
    } catch (e) {
      console.error('Error al cargar solicitantes frecuentes:', e);
    }
  }

  selectTopSolicitante(nombre: string) {
    // Buscar si existe en la lista completa para enlazar el ID correspondiente
    const match = this.solicitantesList.find(s => s.nombre.toLowerCase().trim() === nombre.toLowerCase().trim());
    const id = match ? match.id : 0;
    this.handleSolicitanteSelection(nombre, id);
  }

  get hasSingleClientForSolicitante(): boolean {
    return (Number(this.selectedSolicitanteId) || 0) > 0 && this.clientesFiltradosPorSolicitante.length === 1;
  }

  handleSolicitanteSelection(nombre: string, id: number) {
    this.formData.solicitante = nombre;
    this.selectedSolicitanteId = id;

    // Buscar clientes enlazados a este solicitante en la lista completa
    const matches = this.solicitantesList.filter(s => 
      (s.id === id || s.nombre.toLowerCase().trim() === nombre.toLowerCase().trim()) && s.clienteId > 0
    );
    const clientIds = Array.from(new Set(matches.map(m => m.clienteId)));
    const linkedClients = this.clientesList.filter(c => clientIds.includes(c.id));

    this.clientesFiltradosPorSolicitante = linkedClients;

    if (linkedClients.length === 1) {
      // Si tiene exactamente 1 cliente, lo preseleccionamos y avanzamos directamente al paso 3
      this.selectCliente(linkedClients[0]);
      setTimeout(() => {
        if (this.currentStep === 1) {
          this.stepDirection = 'forward';
          this.currentStep = 3;
        }
      }, 200);
    } else if (linkedClients.length > 1) {
      // Si tiene varios clientes, limpiamos si el actual no pertenece a la lista y vamos al paso 2
      if (this.selectedClienteId && !clientIds.includes(this.selectedClienteId)) {
        this.clearCliente();
      }
      setTimeout(() => {
        if (this.currentStep === 1) {
          this.stepDirection = 'forward';
          this.currentStep = 2;
        }
      }, 200);
    } else {
      // Si no tiene cliente enlazado específico en catálogo, mostramos todos y vamos al paso 2
      this.clientesFiltradosPorSolicitante = this.clientesList;
      setTimeout(() => {
        if (this.currentStep === 1) {
          this.stepDirection = 'forward';
          this.currentStep = 2;
        }
      }, 200);
    }
  }


  constructor(
    private modalCtrl: ModalController,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private metodosUi: Metodosui,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    // Escuchar múltiples eventos de teclado de Capacitor para asegurar detección en todas las plataformas
    window.addEventListener('keyboardWillShow', this._onKeyboardShow);
    window.addEventListener('keyboardDidShow', this._onKeyboardShow);
    window.addEventListener('keyboardWillHide', this._onKeyboardHide);
    window.addEventListener('keyboardDidHide', this._onKeyboardHide);

    this.loadClientesList();
    this.loadSolicitantesList();
    this.loadTopSolicitantes();

    if (this.inspeccionData) {
      if (this.mode === 'edit') {
        this.formData.id = Number(this.inspeccionData.dt1) || 0;
        this.formData.direccion = this.inspeccionData.dt3 || '';
        this.formData.solicitante = this.inspeccionData.dt5 || '';
        this.formData.estado = this.inspeccionData.dt6 || 'Borrador';
        this.formData.fecha_creacion = this.inspeccionData.dt7 ? new Date(this.inspeccionData.dt7).toISOString().split('T')[0] : new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
        this.formData.incluyeMaterial = this.inspeccionData.dt13 === 'Y';
        this.selectedDireccionId = Number(this.inspeccionData.dt9) || 0;
        this.selectedSolicitanteId = Number(this.inspeccionData.dt10) || 0;
      }

      this.formData.cliente = this.inspeccionData.dt2 || this.formData.cliente || '';
      this.selectedClienteId = Number(this.inspeccionData.dt8) || this.selectedClienteId || 0;

      if (this.selectedClienteId) {
        this.loadDireccionesByCliente(this.selectedClienteId);
      }
    }
  }

  ngOnDestroy() {
    window.removeEventListener('keyboardWillShow', this._onKeyboardShow);
    window.removeEventListener('keyboardDidShow', this._onKeyboardShow);
    window.removeEventListener('keyboardWillHide', this._onKeyboardHide);
    window.removeEventListener('keyboardDidHide', this._onKeyboardHide);
  }

  // ── Lists Loading ────────────────────────────────────────────────────────
  async loadClientesList() {
    try {
      const data: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral('selDtsList', 'listClientes', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '')
        )
      );
      if (Array.isArray(data)) {
        this.clientesList = data.map(c => ({ id: Number(c.dt1), nombre: c.dt2 }));

        // Sort alphabetically first
        this.clientesList.sort((a, b) => a.nombre.localeCompare(b.nombre));

        // Prioritize "Inmobiliaria Don Antonio"
        const index = this.clientesList.findIndex(c => c.nombre.trim().toLowerCase() === 'inmobiliaria don antonio');
        if (index > -1) {
          const prioritized = this.clientesList.splice(index, 1)[0];
          this.clientesList.unshift(prioritized);
        }
        
        // Match by name if we have the name but not the ID (legacy or just loaded)
        if (this.formData.cliente && !this.selectedClienteId) {
            const match = this.clientesList.find(c => c.nombre.toLowerCase().trim() === this.formData.cliente.toLowerCase().trim());
            if (match) {
                this.selectedClienteId = match.id;
                this.loadDireccionesByCliente(match.id);
            }
        }
      }
    } catch (e) { console.error('Error loading clientes', e); }
  }

  async loadDireccionesByCliente(clienteId: number) {
    try {
      const data: any = await firstValueFrom(
        this.javapiService.executeAdminCrud('DIRECCION', 'SELECT_ALL', { dt1: '' })
      );
      if (Array.isArray(data)) {
        // dt1: id, dt2: id_cliente, dt3: id_grupo, dt5: nombre
        const all = data.map(d => ({ 
          id: Number(d.dt1), 
          clienteId: Number(d.dt2), 
          grupoId: Number(d.dt3), 
          nombre: d.dt5 
        }));

        // Obtener el grupo del solicitante seleccionado si existe
        const solicitante = this.solicitantesList.find(s => s.id === this.selectedSolicitanteId);
        const solicitanteGrupoId = solicitante ? solicitante.grupoId : 0;

        // Filtrar direcciones por cliente y si tiene grupo el solicitante, también por ese grupo
        this.direccionesList = all.filter(d => 
          d.clienteId === clienteId && 
          (!solicitanteGrupoId || d.grupoId === solicitanteGrupoId)
        );

        if (this.formData.direccion && !this.selectedDireccionId) {
             const match = this.direccionesList.find(d => d.nombre.toLowerCase().trim() === this.formData.direccion.toLowerCase().trim());
             if (match) this.selectedDireccionId = match.id;
        }

        // Cargar Top 10 de direcciones frecuentes
        this.loadTopDirecciones(clienteId);
      }
    } catch (e) { console.error('Error loading direcciones', e); }
  }

  async loadSolicitantesList() {
    try {
      const data: any = await firstValueFrom(
        this.javapiService.executeAdminCrud('SOLICITANTE', 'SELECT_ALL', { dt1: '' })
      );
      if (Array.isArray(data)) {
        this.solicitantesList = data.map(c => ({ 
          id: Number(c.dt1), 
          nombre: c.dt2,
          grupoId: Number(c.dt3), // Guardar el id_grupo
          clienteId: Number(c.dt6)
        }));
        
        if (this.formData.solicitante && !this.selectedSolicitanteId) {
            const match = this.solicitantesList.find(s => s.nombre.toLowerCase().trim() === this.formData.solicitante.toLowerCase().trim());
            if (match) this.selectedSolicitanteId = match.id;
        }
      }
    } catch (e) { console.error('Error loading solicitantes', e); }
  }

  selectCliente(c: any) {
    this.formData.cliente = c.nombre;
    this.selectedClienteId = c.id;
    this.formData.direccion = '';
    this.selectedDireccionId = 0;
    this.loadDireccionesByCliente(c.id);
  }

  selectDireccion(d: any) {
    this.formData.direccion = d.nombre;
    this.selectedDireccionId = d.id;
  }

  selectSolicitante(s: any) {
    this.handleSolicitanteSelection(s.nombre, s.id);
  }

  private async showToast(message: string, color: string) {
    const toast = await this.toastCtrl.create({
      message,
      duration: 2000,
      color,
      position: 'bottom'
    });
    await toast.present();
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  /** ¿Puede avanzar al siguiente paso? */
  canAdvance(): boolean {
    if (this.currentStep === 1) return (Number(this.selectedSolicitanteId) || 0) > 0;
    if (this.currentStep === 2) return (Number(this.selectedClienteId) || 0) > 0;
    if (this.currentStep === 3) return (Number(this.selectedDireccionId) || 0) > 0;
    return true; // Paso 4 siempre puede guardar
  }

  nextStep() {
    if (!this.canAdvance()) return;
    if (this.currentStep < this.totalSteps) {
      this.stepDirection = 'forward';
      if (this.currentStep === 1 && this.hasSingleClientForSolicitante) {
        this.currentStep = 3;
      } else {
        this.currentStep++;
      }
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.stepDirection = 'back';
      if (this.currentStep === 3 && this.hasSingleClientForSolicitante) {
        this.currentStep = 1;
      } else {
        this.currentStep--;
      }
    }
  }

  isFormValid(): boolean {
    return (Number(this.selectedClienteId) || 0) > 0 && 
           (Number(this.selectedDireccionId) || 0) > 0 && 
           (Number(this.selectedSolicitanteId) || 0) > 0;
  }

  async save() {
    if (!this.isFormValid()) {
      const toast = await this.toastCtrl.create({
        message: 'Por favor complete Cliente, Dirección y Solicitante del catálogo.',
        duration: 2000,
        color: 'warning'
      });
      await toast.present();
      return;
    }

    try {
      const payload = {
        usuario: localStorage.getItem('user_pGarcia') || 'admin',
        trabajoP: {
          rutina: 'inspecciones',
          subrutina: this.mode === 'add' ? 'insert' : 'update'
        },
        trabajoD: {
          id: this.formData.id.toString(),
          cliente: this.formData.cliente,
          lugar: this.formData.direccion,
          clienteId: this.selectedClienteId,
          direccionId: this.selectedDireccionId,
          solicitanteId: this.selectedSolicitanteId,
          solicitante: this.formData.solicitante,
          estado: this.formData.estado,
          incluye_material: this.formData.incluyeMaterial ? 'Y' : 'N'
        }
      };

      this.isSaving = true;

      const res: any = await firstValueFrom(this.javapiService.insertaTrabajo(payload));
      if (res && (res.success || res.id)) {
        if (res.id) {
          this.formData.id = Number(res.id);
        }
        this.metodosUi.showToast('Inspección guardada correctamente', 'success');
        this.modalCtrl.dismiss({ ...this.formData, newId: res.id ? Number(res.id) : this.formData.id });
      }
    } catch (e) {
      console.error('Error saving inspection', e);
    } finally {
      this.isSaving = false;
    }
  }

  async openGenericSelector(entity: 'SOLICITANTE' | 'CLIENTE' | 'DIRECCION', title: string, placeholder: string, itemsList: any[], extraData: any = null) {
    const currentId = entity === 'SOLICITANTE' ? this.selectedSolicitanteId
                    : entity === 'CLIENTE' ? this.selectedClienteId
                    : this.selectedDireccionId;

    const modal = await this.modalCtrl.create({
      component: ComponentesGenericosComponent,
      componentProps: {
        entity: entity,
        title: title,
        placeholder: placeholder,
        items: itemsList,
        initialSearch: '',
        selectedIds: currentId ? [currentId] : [],
        extraData: extraData
      }
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data && data.selected) {
      const selected = data.selected;
      
      if (entity === 'SOLICITANTE') {
        if (selected.isNew) {
          // Si es creado nuevo, recargamos la lista completa y seleccionamos el nuevo id
          await this.loadSolicitantesList();
          const match = this.solicitantesList.find(s => s.nombre.toLowerCase().trim() === selected.nombre.toLowerCase().trim());
          if (match) {
            this.handleSolicitanteSelection(match.nombre, match.id);
          } else {
            this.handleSolicitanteSelection(selected.nombre, Number(selected.data.id) || 0);
          }
        } else {
          this.handleSolicitanteSelection(selected.nombre, selected.id);
        }
      } 
      
      else if (entity === 'CLIENTE') {
        if (selected.isNew) {
          await this.loadClientesList();
          const match = this.clientesList.find(c => c.nombre.toLowerCase().trim() === selected.nombre.toLowerCase().trim());
          if (match) {
            this.selectCliente(match);
          } else {
            this.selectCliente({ id: Number(selected.data.id) || 0, nombre: selected.nombre });
          }
        } else {
          this.selectCliente(selected);
        }
      } 
      
      else if (entity === 'DIRECCION') {
        if (selected.isNew) {
          await this.loadDireccionesByCliente(this.selectedClienteId);
          const match = this.direccionesList.find(d => d.nombre.toLowerCase().trim() === selected.nombre.toLowerCase().trim());
          if (match) {
            this.selectDireccion(match);
          } else {
            this.selectDireccion({ id: Number(selected.data.id) || 0, nombre: selected.nombre, id_cliente: this.selectedClienteId });
          }
        } else {
          this.selectDireccion(selected);
        }
      }
    }
  }



  clearCliente() { this.formData.cliente = ''; this.selectedClienteId = 0; }
  clearDireccion() { this.formData.direccion = ''; this.selectedDireccionId = 0; }
  clearSolicitante() { 
    this.formData.solicitante = ''; 
    this.selectedSolicitanteId = 0; 
    this.clientesFiltradosPorSolicitante = [];
    this.clearCliente();
  }
}
